/**
 * SystemSettings - System & Daten zurücksetzen
 * Extrahiert aus SettingsPage.js für bessere Wartbarkeit
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SystemSettings = () => {
  const handleResetTransactions = async () => {
    const confirmed = window.confirm(
      '⚠️ WARNUNG: Alle Transaktionsdaten werden gelöscht!\n\n' +
      'Folgende Daten werden entfernt:\n' +
      '• Alle Termine\n' +
      '• Alle Rechnungen\n' +
      '• Alle Zahlungen\n' +
      '• Alle Punkte-Transaktionen\n' +
      '• Alle Abonnements\n\n' +
      'Kunden bleiben erhalten, aber ihre Punkte werden auf 0 gesetzt.\n\n' +
      'Möchten Sie fortfahren?'
    );
    if (!confirmed) return;
    
    const finalConfirm = window.prompt(
      'Zur Bestätigung geben Sie bitte "RESET" ein:'
    );
    if (finalConfirm !== 'RESET') {
      toast.error('Reset abgebrochen - Bestätigung nicht korrekt');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/settings/reset-data`, {
        include_customers: false,
        confirmation: 'RESET'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`✅ Daten zurückgesetzt!\n${JSON.stringify(response.data.deleted)}`);
      console.log('Reset result:', response.data);
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleResetAllKeepTemplates = async () => {
    const confirmed = window.confirm(
      '⚠️ WARNUNG: Alle Daten inkl. Kunden werden gelöscht!\n\n' +
      'Folgende Daten werden UNWIDERRUFLICH entfernt:\n' +
      '• Alle Termine\n' +
      '• Alle Rechnungen & Zahlungen\n' +
      '• Alle Punkte-Transaktionen\n' +
      '• Alle Abonnements\n' +
      '• Alle Wartelisten-Einträge\n' +
      '• Alle Miet-Rechnungen\n' +
      '• ⚠️ ALLE KUNDEN\n' +
      '• ⚠️ ALLE BENUTZER (ausser Admins)\n\n' +
      '✅ BEHALTEN WERDEN:\n' +
      '• Email-Templates\n' +
      '• WhatsApp-Templates\n' +
      '• Automation Rules\n' +
      '• Services & Pakete\n' +
      '• Einstellungen\n\n' +
      'Möchten Sie fortfahren?'
    );
    if (!confirmed) return;
    
    const finalConfirm = window.prompt(
      'Zur Bestätigung geben Sie bitte "RESET" ein:'
    );
    if (finalConfirm !== 'RESET') {
      toast.error('Reset abgebrochen - Bestätigung nicht korrekt');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/settings/reset-data`, {
        include_customers: true,
        keep_templates: true,
        confirmation: 'RESET'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('✅ Alle Daten wurden zurückgesetzt (Templates behalten)!');
      console.log('Reset result:', response.data);
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleResetAll = async () => {
    const confirmed = window.confirm(
      '🚨 ACHTUNG: ALLE DATEN werden gelöscht!\n\n' +
      'Folgende Daten werden UNWIDERRUFLICH entfernt:\n' +
      '• Alle Termine\n' +
      '• Alle Rechnungen & Zahlungen\n' +
      '• Alle Punkte-Transaktionen\n' +
      '• Alle Abonnements\n' +
      '• ⚠️ ALLE KUNDEN\n' +
      '• ⚠️ ALLE BENUTZER (ausser Admins)\n' +
      '• ⚠️ Alle Zahlungsbeziehungen\n' +
      '• ⚠️ Alle Kreditkarten\n\n' +
      'Dies kann NICHT rückgängig gemacht werden!\n\n' +
      'Möchten Sie wirklich fortfahren?'
    );
    if (!confirmed) return;
    
    const doubleConfirm = window.confirm(
      '🛑 LETZTE WARNUNG!\n\n' +
      'Sie sind dabei, ALLE Kunden und deren Daten zu löschen.\n\n' +
      'Sind Sie ABSOLUT sicher?'
    );
    if (!doubleConfirm) return;
    
    const finalConfirm = window.prompt(
      'Zur endgültigen Bestätigung geben Sie bitte "RESET" ein:'
    );
    if (finalConfirm !== 'RESET') {
      toast.error('Reset abgebrochen - Bestätigung nicht korrekt');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/settings/reset-data`, {
        include_customers: true,
        confirmation: 'RESET'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('✅ Alle Daten wurden zurückgesetzt!');
      console.log('Full reset result:', response.data);
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <Card className="border-red-300">
      <CardHeader className="bg-red-50">
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle size={20} />
              System & Daten zurücksetzen
            </CardTitle>
            <CardDescription className="text-red-600">
              ⚠️ Achtung: Diese Aktionen können nicht rückgängig gemacht werden!
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6 pt-6">
        {/* Reset ohne Kunden */}
        <div className="p-4 border border-orange-300 rounded-lg bg-orange-50">
          <h3 className="font-semibold text-orange-800 mb-2 flex items-center gap-2">
            <RefreshCw size={18} />
            Transaktionsdaten zurücksetzen
          </h3>
          <p className="text-sm text-orange-700 mb-4">
            Löscht alle Termine, Rechnungen, Zahlungen, Punkte-Transaktionen und Abonnements. 
            <strong> Kunden bleiben erhalten</strong>, aber ihre Punkte werden auf 0 gesetzt.
          </p>
          <Button
            variant="outline"
            className="border-orange-500 text-orange-700 hover:bg-orange-100"
            onClick={handleResetTransactions}
          >
            <RefreshCw size={16} className="mr-2" />
            Transaktionen zurücksetzen (Kunden behalten)
          </Button>
        </div>

        {/* Reset mit Kunden aber Templates behalten */}
        <div className="p-4 border-2 border-purple-400 rounded-lg bg-purple-50">
          <h3 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
            <RefreshCw size={18} />
            ALLES zurücksetzen (Templates behalten)
          </h3>
          <p className="text-sm text-purple-700 mb-4">
            Löscht <strong>ALLE Daten inkl. Kunden und Personal (Trainer)</strong>, aber behält Email-Templates, WhatsApp-Templates 
            und Automation Rules. <strong>Ideal für Neustart mit bestehender Konfiguration.</strong>
          </p>
          <div className="text-xs text-purple-600 mb-3 bg-purple-100 p-2 rounded">
            <strong>Wird gelöscht:</strong> Termine, Rechnungen, Zahlungen, Kunden, <span className="text-red-600 font-semibold">Personal/Trainer</span>, Warteliste, Miet-Rechnungen, Achievements, Anamnese<br/>
            <strong>Bleibt erhalten:</strong> Email-Templates, WhatsApp-Templates, Automation Rules, Services, Pakete, Einstellungen, <span className="text-green-600 font-semibold">Admin-Benutzer</span>
          </div>
          <Button
            variant="outline"
            className="border-purple-500 text-purple-700 hover:bg-purple-100"
            onClick={handleResetAllKeepTemplates}
          >
            <RefreshCw size={16} className="mr-2" />
            ALLES zurücksetzen (Templates behalten)
          </Button>
        </div>

        {/* Reset mit Kunden */}
        <div className="p-4 border-2 border-red-400 rounded-lg bg-red-50">
          <h3 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
            <AlertTriangle size={18} />
            ALLE Daten zurücksetzen (inkl. Kunden)
          </h3>
          <p className="text-sm text-red-700 mb-4">
            Löscht <strong>ALLE Daten</strong> inklusive Kunden, Benutzer (ausser Admins), 
            Zahlungsbeziehungen und Kreditkarten. <strong>Nur für komplettes Zurücksetzen!</strong>
          </p>
          <Button
            variant="destructive"
            className="bg-red-600 hover:bg-red-700"
            onClick={handleResetAll}
          >
            <AlertTriangle size={16} className="mr-2" />
            ALLES zurücksetzen (inkl. Kunden)
          </Button>
        </div>

        {/* Info Box */}
        <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
          <h4 className="font-semibold text-blue-800 mb-2">ℹ️ Hinweis</h4>
          <ul className="text-sm text-blue-700 space-y-1 list-disc pl-4">
            <li>Admin-Benutzer werden nie gelöscht</li>
            <li>Einstellungen, Services und Pakete bleiben erhalten</li>
            <li>Diese Funktion ist nur für Test-Zwecke gedacht</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default SystemSettings;
